import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { Device, DeviceInfo } from '@capacitor/device';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly platform = inject(Platform);

  /* Angular */
  public isSSR: boolean = false;
  public isBrowser: boolean = false;

  /* Ionic */
  public isAndroid: boolean = false;
  public isIos: boolean = false;
  public isMobile: boolean = false;
  public isMobileWeb: boolean = false;
  public isDesktop: boolean = false;
  public isCordova: boolean = false;
  public isCapacitor: boolean = false;
  public isDevice: boolean = false; // TODO: isHybrid

  public isAndroidApp: boolean = false;
  public isIosApp: boolean = false;
  public isAndroidWeb: boolean = false;
  public isIosWeb: boolean = false;
  public isIphoneWeb: boolean = false;

  /* Capacitor Core */
  public capacitorPlatform: string;
  public isNativePlatform: boolean;
  // public isWeb: boolean = false;

  /* Capacitor Device */
  public isIosSimulator: boolean = false;

  /* User agent */
  public userAgent: string = (typeof navigator !== 'undefined') ? navigator.userAgent : 'SSR';

  public isMacintosh: boolean = this.userAgent.includes('Macintosh');
  public isSafari: boolean = this.userAgent.includes('Safari') && !/(Chrome|Chromium|CriOS|Edg|OPR)/.test(this.userAgent);
  public isSafariDesktop: boolean = this.isMacintosh && this.isSafari && !/Mobile/.test(this.userAgent);
  public isSafariDevice: boolean = /i(Phone|Pad|Pod)/.test(this.userAgent) && this.isSafari && (typeof navigator !== 'undefined' ? (navigator.platform !== 'Win32') : false);
  private readonly isKeyboardVisibleSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isKeyboardVisible$: Observable<boolean> = this.isKeyboardVisibleSubject.asObservable();
  private readonly keyboardHeightSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly keyboardHeight$: Observable<number> = this.keyboardHeightSubject.asObservable();
  private viewportKeyboardListenersAttached: boolean = false;
  private viewportKeyboardHeight: number = 0;
  private nativeKeyboardHeight: number = 0;

  constructor() {
    this.isSSR = (typeof window === 'undefined');

    this.checkWithAngular();
    this.checkWithIonic();
    this.checkWithCapacitorCore();
    void this.checkWithCapacitorDevice();

    if (this.isBrowser) {
      this.attachViewportKeyboardListeners();
      this.attachIonicKeyboardListeners();
      this.attachCapacitorKeyboardListeners();
    }
  }

  // Relay Ionic keyboard events to the shared visibility signal.
  private attachIonicKeyboardListeners(): void {
    this.platform.keyboardDidShow.subscribe((): void => {
      this.isKeyboardVisibleSubject.next(true);
    });

    this.platform.keyboardDidHide.subscribe((): void => {
      this.isKeyboardVisibleSubject.next(false);
      this.resetKeyboardHeight();
    });
  }

  // Merge native KeyboardInfo with visualViewport changes.
  // On native iOS with resize='ionic' the viewport overlap can stay at 0,
  // while Capacitor still reports the real keyboard height.
  private attachCapacitorKeyboardListeners(): void {
    if (!Capacitor.isPluginAvailable('Keyboard')) {
      return;
    }

    Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
      this.isKeyboardVisibleSubject.next(true);
      this.nativeKeyboardHeight = Math.max(0, info.keyboardHeight);
      this.syncKeyboardHeight();
    });

    Keyboard.addListener('keyboardWillHide', () => {
      this.isKeyboardVisibleSubject.next(false);
      this.nativeKeyboardHeight = 0;
      this.syncKeyboardHeight();
    });
  }

  private attachViewportKeyboardListeners(): void {
    if (this.viewportKeyboardListenersAttached) return;
    if (typeof window === 'undefined') return;
    const viewport = window.visualViewport;
    if (!viewport) return;
    this.viewportKeyboardListenersAttached = true;
    const update = (): void => {
      this.viewportKeyboardHeight = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      this.syncKeyboardHeight();
    };
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    window.addEventListener('orientationchange', update);
    update();
  }

  private syncKeyboardHeight(): void {
    this.keyboardHeightSubject.next(Math.max(this.viewportKeyboardHeight, this.nativeKeyboardHeight));
  }

  private resetKeyboardHeight(): void {
    this.viewportKeyboardHeight = 0;
    this.nativeKeyboardHeight = 0;
    this.keyboardHeightSubject.next(0);
  }

  /* Angular */

  private checkWithAngular(): void {
    if (!environment.production) {
      console.log('Angular platform:', this.platformId);
    }
    // this.isSSR = isPlatformServer(this.platformId);
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  /* Ionic */

  private checkWithIonic(): void {
    if (!environment.production) {
      console.log('Ionic platforms: ', this.platform.platforms());
    }

    this.isAndroid = this.platform.is('android');
    // console.log('isAndroid: ', this.isAndroid);
    this.isIos = this.platform.is('ios');
    // console.log('isIos: ', this.isIos);
    this.isMobile = this.platform.is('mobile');
    // console.log('isMobile: ', this.isMobile);
    this.isMobileWeb = this.platform.is('mobileweb');
    // console.log('isMobileWeb: ', this.isMobileWeb);
    this.isDesktop = this.platform.is('desktop');
    // console.log('isDesktop: ', this.isDesktop);
    this.isCordova = this.platform.is('cordova');
    // console.log('isCordova: ', this.isCordova);
    this.isCapacitor = this.platform.is('capacitor');
    // console.log('isCapacitor: ', this.isCapacitor);
    this.isDevice = this.platform.is('hybrid');
    // console.log('isDevice: ', this.isDevice);

    this.isAndroidApp = this.isAndroid && this.isDevice;
    // console.log('isAndroidApp: ', this.isAndroidApp);
    this.isIosApp = this.isIos && this.isDevice;
    // console.log('isIosApp: ', this.isIosApp);
    this.isAndroidWeb = this.isAndroid && !this.isDevice;
    // console.log('isAndroidWeb: ', this.isAndroidWeb);
    this.isIosWeb = this.isIos && !this.isDevice;
    // console.log('isIosWeb: ', this.isIosWeb);
    this.isIphoneWeb = this.isIosWeb && /iPhone/.test(this.userAgent);
  }

  /* Capacitor Core */

  private checkWithCapacitorCore(): void {
    this.capacitorPlatform = Capacitor.getPlatform();
    this.isNativePlatform = Capacitor.isNativePlatform();

    if (!environment.production) {
      console.log(`Capacitor platform: ${this.capacitorPlatform}`);
      console.log(`isNativePlatform: ${this.isNativePlatform}`);
    }
    // this.isAndroid = Capacitor.getPlatform() === 'android';
    // this.isIos = Capacitor.getPlatform() === 'ios';
    // this.isWeb = Capacitor.getPlatform() === 'web';
  }

  /* Capacitor Device */

  private async checkWithCapacitorDevice(): Promise<void> {
    if (this.isSSR) return;

    try {
      const deviceInfo: DeviceInfo = await Device.getInfo();
      this.isIosSimulator = deviceInfo.platform === 'ios' && deviceInfo.isVirtual;
    } catch (error) {
      console.error(error);
    }
  }
}
